import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { fetchMyOrders, fetchOrderDelivery } from "@/lib/api";
import { Package, ChevronDown, Terminal } from "lucide-react";

const STATUS_COLORS = {
  paid: "bg-emerald-500/15 text-emerald-300",
  pending: "bg-yellow-500/15 text-yellow-300",
  cancelled: "bg-red-500/15 text-red-300",
};

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);
  const [delivery, setDelivery] = useState({});

  useEffect(() => { if (user) fetchMyOrders().then(setOrders).catch(() => setOrders([])); }, [user]);

  const toggle = async (n) => {
    if (open === n) { setOpen(null); return; }
    setOpen(n);
    if (!delivery[n]) {
      try { const d = await fetchOrderDelivery(n); setDelivery((prev) => ({ ...prev, [n]: d })); } catch { /* ignore */ }
    }
  };

  if (!user) return <div className="pt-32 pb-24 text-center text-white/60" data-testid="orders-login">Please sign in. <Link to="/store" className="text-[#FFBF00]">Store</Link></div>;

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-orders">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl mb-8 flex items-center gap-3"><Package /> My Orders</h1>
        {orders === null ? (
          <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 rounded-2xl glass skeleton" />)}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 text-white/50" data-testid="orders-empty">No orders yet. <Link to="/store" className="text-[#FFBF00]">Start shopping</Link></div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.order_number} className="glass rounded-2xl overflow-hidden" data-testid={`order-${o.order_number}`}>
                <button onClick={() => toggle(o.order_number)} className="w-full flex items-center gap-4 p-5 text-left">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[#FFBF00]">{o.order_number}</div>
                    <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleString()} · {o.items.length} item(s)</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display">₹{o.total_inr.toFixed(2)}{o.dc_used > 0 && <span className="text-[#FFBF00] text-sm"> + {o.dc_used} DC</span>}</div>
                    <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs ${STATUS_COLORS[o.status] || "bg-white/10"}`}>{o.status}</span>
                  </div>
                  <ChevronDown size={18} className={`transition-transform ${open === o.order_number ? "rotate-180" : ""}`} />
                </button>
                {open === o.order_number && (
                  <div className="px-5 pb-5 border-t border-white/5 pt-4">
                    <div className="space-y-2 mb-4">
                      {o.items.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-white/70">{it.name} × {it.qty}</span>
                          <span>₹{(it.price_inr * it.qty).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    {delivery[o.order_number]?.deliveries?.length > 0 && (
                      <div className="rounded-xl bg-black/40 border border-white/10 p-4">
                        <div className="text-xs uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2"><Terminal size={14} /> Delivery ({o.status === "paid" ? "run /lostdcstore in-game" : "pending payment"})</div>
                        {delivery[o.order_number].deliveries.map((d) => (
                          <div key={d.id} className="flex items-center justify-between text-xs py-1 gap-2">
                            <code className="text-[#FFBF00] truncate">{d.command}</code>
                            <span className={`px-2 py-0.5 rounded-full shrink-0 ${d.status === "claimed" ? "bg-emerald-500/15 text-emerald-300" : "bg-yellow-500/15 text-yellow-300"}`}>{d.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
